import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send,
  Bot,
  User,
  Paperclip,
  X,
  Sparkles,
  ClipboardList,
  ShoppingBag,
  BarChart3,
  Wand2,
  ArrowRight,
} from 'lucide-react'
import { ChatMessage } from '../../types'

// Helper function to render markdown-style formatted text
function renderFormattedText(text: string): React.ReactNode {
  // Split text into lines for processing
  const lines = text.split('\n')
  
  return lines.map((line, lineIndex) => {
    // Check for headers (### Header)
    if (line.startsWith('### ')) {
      return (
        <div key={lineIndex} className="font-bold text-sm mt-2 mb-1 text-primary-600 dark:text-primary-400">
          {renderInlineFormatting(line.substring(4))}
        </div>
      )
    }
    
    // Check for subheaders (## Header)
    if (line.startsWith('## ')) {
      return (
        <div key={lineIndex} className="font-bold text-sm mt-2 mb-1">
          {renderInlineFormatting(line.substring(3))}
        </div>
      )
    }
    
    // Check for bullet points (* item or - item)
    if (line.match(/^\s*[\*\-•]\s+/)) {
      const content = line.replace(/^\s*[\*\-•]\s+/, '')
      return (
        <div key={lineIndex} className="flex items-start gap-1.5 ml-2">
          <span className="text-primary-500 mt-0.5">•</span>
          <span>{renderInlineFormatting(content)}</span>
        </div>
      )
    }
    
    // Check for horizontal rule (---)
    if (line.match(/^-{3,}$/)) {
      return <hr key={lineIndex} className="my-2 border-gray-300 dark:border-gray-600" />
    }
    
    // Empty line
    if (line.trim() === '') {
      return <div key={lineIndex} className="h-2" />
    }
    
    // Regular line with inline formatting
    return (
      <div key={lineIndex}>
        {renderInlineFormatting(line)}
      </div>
    )
  })
}

// Helper function to render inline formatting (bold, italic, etc.)
function renderInlineFormatting(text: string): React.ReactNode {
  // Pattern to match **bold** text
  const boldPattern = /\*\*([^*]+)\*\*/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match
  let keyIndex = 0
  
  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }
    
    // Add the bold text
    parts.push(
      <strong key={`bold-${keyIndex++}`} className="font-semibold">
        {match[1]}
      </strong>
    )
    
    lastIndex = match.index + match[0].length
  }
  
  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }
  
  // If no formatting was found, return original text
  if (parts.length === 0) {
    return text
  }
  
  return <>{parts}</>
}

interface ChatPanelProps {
  messages: ChatMessage[]
  onSendMessage: (message: string, files?: File[]) => void
  isLoading: boolean
  currentInput?: string
  activeAgent?: string | null
}

export function ChatPanel({ messages, onSendMessage, isLoading, currentInput, activeAgent }: ChatPanelProps) {
  const [input, setInput] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Generate context-aware progress steps based on user input
  const getProgressSteps = (userMessage: string) => {
    const lowerMessage = userMessage.toLowerCase()
    
    // Ad copy related requests
    if (lowerMessage.includes('ad copy') || lowerMessage.includes('copy') || lowerMessage.includes('headline')) {
      return [
        { text: "Analyzing campaign context...", color: "bg-green-400", delay: 0 },
        { text: "Generating compelling headlines...", color: "bg-blue-400", delay: 0.5 },
        { text: "Crafting persuasive descriptions...", color: "bg-purple-400", delay: 1 },
        { text: "Optimizing CTAs and tone...", color: "bg-orange-400", delay: 1.5 }
      ]
    }
    
    // Creative variants related requests
    if (lowerMessage.includes('creative') || lowerMessage.includes('variant') || lowerMessage.includes('visual')) {
      return [
        { text: "Analyzing ad copy content...", color: "bg-green-400", delay: 0 },
        { text: "Selecting product images...", color: "bg-blue-400", delay: 0.5 },
        { text: "Generating color schemes...", color: "bg-purple-400", delay: 1 },
        { text: "Creating visual layouts...", color: "bg-pink-400", delay: 1.5 }
      ]
    }
    
    // Media plan related requests
    if (lowerMessage.includes('media') || lowerMessage.includes('channel') || lowerMessage.includes('budget')) {
      return [
        { text: "Analyzing campaign objectives...", color: "bg-green-400", delay: 0 },
        { text: "Evaluating channel performance...", color: "bg-blue-400", delay: 0.5 },
        { text: "Calculating budget allocation...", color: "bg-purple-400", delay: 1 },
        { text: "Optimizing reach and frequency...", color: "bg-indigo-400", delay: 1.5 }
      ]
    }
    
    // Audience related requests
    if (lowerMessage.includes('audience') || lowerMessage.includes('target') || lowerMessage.includes('segment')) {
      return [
        { text: "Analyzing demographics...", color: "bg-green-400", delay: 0 },
        { text: "Identifying psychographics...", color: "bg-blue-400", delay: 0.5 },
        { text: "Creating personas...", color: "bg-purple-400", delay: 1 },
        { text: "Optimizing targeting strategy...", color: "bg-teal-400", delay: 1.5 }
      ]
    }
    
    // Template related requests
    if (lowerMessage.includes('template') || lowerMessage.includes('format') || lowerMessage.includes('design')) {
      return [
        { text: "Analyzing campaign requirements...", color: "bg-green-400", delay: 0 },
        { text: "Selecting optimal formats...", color: "bg-blue-400", delay: 0.5 },
        { text: "Matching with campaign context...", color: "bg-purple-400", delay: 1 },
        { text: "Preparing template recommendations...", color: "bg-cyan-400", delay: 1.5 }
      ]
    }
    
    // General campaign optimization
    if (lowerMessage.includes('optimize') || lowerMessage.includes('improve') || lowerMessage.includes('better')) {
      return [
        { text: "Analyzing current performance...", color: "bg-green-400", delay: 0 },
        { text: "Identifying improvement areas...", color: "bg-blue-400", delay: 0.5 },
        { text: "Generating optimization strategies...", color: "bg-purple-400", delay: 1 },
        { text: "Preparing recommendations...", color: "bg-yellow-400", delay: 1.5 }
      ]
    }
    
    // Default progress for general queries
    return [
      { text: "Understanding your request...", color: "bg-green-400", delay: 0 },
      { text: "Analyzing campaign data...", color: "bg-blue-400", delay: 0.5 },
      { text: "Generating insights...", color: "bg-purple-400", delay: 1 },
      { text: "Preparing response...", color: "bg-gray-400", delay: 1.5 }
    ]
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if ((!input.trim() && attachments.length === 0) || isLoading) return
    onSendMessage(input.trim(), attachments.length > 0 ? attachments : undefined)
    setInput('')
    setAttachments([])
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length) setAttachments(prev => [...prev, ...files])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeAttachment = (idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx))
  }

  const autoResizeTextarea = () => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const starterPrompts: Array<{
    icon: React.ReactNode
    title: string
    prompt: string
    accent: string
  }> = [
    {
      icon: <ClipboardList className="h-4 w-4" />,
      title: 'Start a campaign proposal',
      prompt:
        'Help me build a campaign proposal — outline objectives, audience, channels and budget for this advertiser.',
      accent: 'from-primary-500/10 to-blue-500/10 text-primary-600 dark:text-primary-400',
    },
    {
      icon: <ShoppingBag className="h-4 w-4" />,
      title: 'Discover agentic buy inventory',
      prompt:
        'Discover available agentic buy inventory and recommend products that fit my campaign objectives.',
      accent: 'from-amber-500/10 to-orange-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      icon: <BarChart3 className="h-4 w-4" />,
      title: 'Create a media plan',
      prompt:
        "I'd like to create a media plan — suggest channels, placements and budget allocation for this campaign.",
      accent: 'from-emerald-500/10 to-teal-500/10 text-emerald-600 dark:text-emerald-400',
    },
    {
      icon: <Wand2 className="h-4 w-4" />,
      title: 'Generate AI creative',
      prompt:
        'Create AI-generated ad creatives for this campaign — propose concepts, sizes and copy variations.',
      accent: 'from-fuchsia-500/10 to-pink-500/10 text-fuchsia-600 dark:text-fuchsia-400',
    },
  ]

  return (
    <div className="flex flex-col h-full bg-white/95 dark:bg-slate-900/80">
      <div className="p-3 border-b border-slate-200/70 dark:border-slate-800/80 bg-white/95 dark:bg-slate-900/80 flex-shrink-0">
        <div className="flex items-center">
          <Bot className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">AI Assistant</h3>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          Ask about your campaign
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex max-w-[85%] min-w-0 ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`flex-shrink-0 ${message.type === 'user' ? 'ml-2' : 'mr-2'}`}>
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center overflow-hidden ${
                    message.type === 'user' 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {message.type === 'user' ? (
                      <User className="h-3 w-3" />
                    ) : (
                      <img
                        src="/favicon.png"
                        alt="AgenX"
                        className="h-4 w-4 object-contain"
                        draggable={false}
                      />
                    )}
                  </div>
                </div>
                {/* `min-w-0` + `max-w-full` + `overflow-wrap: anywhere`
                    keeps long unbreakable URLs/tokens from blowing out the
                    bubble width past the column's 85% cap. */}
                <div className={`min-w-0 max-w-full px-3 py-2 rounded-lg ${
                  message.type === 'user'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}>
                  <div className="text-xs [overflow-wrap:anywhere]">
                    {message.type === 'user' 
                      ? <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{message.content}</p>
                      : renderFormattedText(message.content)
                    }
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="flex max-w-[85%]">
              <div className="flex-shrink-0 mr-2">
                <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center overflow-hidden">
                  <img
                    src="/favicon.png"
                    alt="AgenX"
                    className="h-4 w-4 object-contain"
                    draggable={false}
                  />
                </div>
              </div>
              <div className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-600 dark:text-gray-400">AgenX is working</span>
                    <div className="flex space-x-1">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {getProgressSteps(currentInput || input).map((step, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: step.delay }}
                        className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400"
                      >
                        <div className={`w-2 h-2 ${step.color} rounded-full animate-pulse`}></div>
                        <span>{step.text}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {messages.length === 0 && !isLoading && (
        <div className="px-3 pb-3">
          <div className="flex items-center gap-1.5 mb-2 px-1">
            <Sparkles className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" />
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Try one of these
            </p>
          </div>
          <div className="space-y-1.5">
            {starterPrompts.map((sp, index) => (
              <button
                key={index}
                onClick={() => onSendMessage(sp.prompt)}
                disabled={isLoading}
                className="group w-full text-left rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-start gap-2.5 px-3 py-2.5">
                  <span
                    className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br ${sp.accent}`}
                  >
                    {sp.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                      {sp.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 leading-snug line-clamp-2">
                      {sp.prompt}
                    </p>
                  </div>
                  <ArrowRight className="flex-shrink-0 h-3.5 w-3.5 text-gray-300 dark:text-gray-600 mt-1 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-slate-200/70 dark:border-slate-800/80 bg-white/95 dark:bg-slate-900/80 flex-shrink-0">
        {/* Attachment preview chips */}
        {attachments.length > 0 && (
          <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5">
            {attachments.map((file, i) => (
              <div key={`${file.name}-${i}`} className="flex items-center gap-1.5 pl-1 pr-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg text-[11px] text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                {file.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(file)} alt="" className="w-5 h-5 rounded object-cover" />
                ) : (
                  <div className="w-5 h-5 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-[8px] font-medium">
                    {file.name.split('.').pop()?.toUpperCase()}
                  </div>
                )}
                <span className="max-w-[80px] truncate">{file.name}</span>
                <button onClick={() => removeAttachment(i)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Agent badge */}
        {activeAgent && (
          <div className="px-3 pt-2 pb-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
              <Sparkles className="h-3 w-3 text-primary-600 dark:text-primary-400" />
              <span className="text-[11px] font-medium text-primary-700 dark:text-primary-400">{activeAgent}</span>
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="p-3">
          <form onSubmit={handleSubmit}>
            <div className={`relative border rounded-xl transition-all duration-200 ${
              isLoading
                ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500'
            }`}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => { setInput(e.target.value); autoResizeTextarea() }}
                onKeyDown={handleKeyDown}
                placeholder={isLoading ? "AgenX is working..." : "Ask about campaign..."}
                disabled={isLoading}
                rows={2}
                className="w-full px-3 pt-2.5 pb-8 text-xs border-0 rounded-xl bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none resize-none disabled:cursor-not-allowed"
                style={{ minHeight: '56px', maxHeight: '120px' }}
              />
              <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={handleFileChange} className="hidden" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isLoading}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Attach files">
                    <Paperclip className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={(!input.trim() && attachments.length === 0) || isLoading}
                  className={`p-1.5 rounded-lg focus:outline-none transition-all duration-200 ${
                    (!input.trim() && attachments.length === 0) || isLoading
                      ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-md'
                  }`}
                >
                  {isLoading ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            </div>
          </form>
          {isLoading && (
            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">Please wait while AI processes your request...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
